/**
 * Photo Upload Manager
 *
 * Isolated, background photo upload pipeline. Photos NEVER live in the main
 * offline_queue (which carries text saves). They live in their own IndexedDB
 * store and are uploaded one-at-a-time with their own retry/error handling.
 *
 * Why this exists (SolidHome bug, Feb 2026):
 *   Previously, base64 photo dataUrls were embedded in field-notes sync
 *   payloads. Every text save also re-uploaded every photo. A single 5 MB
 *   photo would block the main thread for 500 ms per IndexedDB read, and
 *   one bad photo blocked ALL subsequent text saves.
 *
 * How it works now:
 *   1. User adds a photo → it's saved to PHOTO_UPLOADS store with status='pending'
 *      AND added to the local field-notes draft (with a local id reference).
 *   2. Background processor picks it up, uploads to the server, marks status='done'.
 *      Field-notes draft photos array is updated with the server URL.
 *   3. Text saves go through with photos referenced by URL only — tiny payloads.
 *   4. If upload fails (network/5xx) → exponential backoff retry, never blocks text.
 *   5. If upload fails (4xx/validation) → marked failed, surfaces in Sync Queue page.
 */

import api from './api';
import { put, getAll, remove, getByIndex, STORES } from './offlineDB';
import { logSync } from './syncLogger';

const PHOTO_UPLOAD_TIMEOUT_MS = 45_000;
// Capped at 60s so flaky cellular doesn't trap users in 15-minute silent
// retries. Photos retry forever; just more frequently. See SolidHome
// Feb 2026 post-mortem.
const BACKOFF_LADDER_MS = [1_000, 4_000, 10_000, 30_000, 60_000];

/** Queue a photo for background upload. Returns the photo id. */
export async function queuePhotoUpload({ siteVisitId, orgId, dataUrl, name, tag }) {
  const id = `photo_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  await put(STORES.PHOTO_UPLOADS, {
    id,
    site_visit_id: siteVisitId,
    org_id: orgId,
    data_url: dataUrl,
    name: name || 'photo.jpg',
    tag: tag || '',
    status: 'pending',
    retries: 0,
    next_attempt_at: new Date().toISOString(),
    last_error: null,
    last_error_class: null,
    created_at: new Date().toISOString(),
  });
  logSync('info', 'photo.queued', { photo_id: id, site_visit_id: siteVisitId, size_kb: Math.round((dataUrl?.length || 0) / 1024) });
  return id;
}

/** Get pending photo uploads for a given site visit (or all if siteVisitId is null). */
export async function getPendingPhotoUploads(siteVisitId = null) {
  let photos;
  if (siteVisitId) {
    photos = await getByIndex(STORES.PHOTO_UPLOADS, 'site_visit_id', siteVisitId);
  } else {
    photos = await getAll(STORES.PHOTO_UPLOADS);
  }
  return photos.filter(p => p.status === 'pending');
}

export async function getAllPhotoUploads() {
  return getAll(STORES.PHOTO_UPLOADS);
}

export async function getFailedPhotoUploads() {
  const all = await getAll(STORES.PHOTO_UPLOADS);
  return all.filter(p => p.status === 'failed');
}

export async function getPhotoUploadCount() {
  const all = await getAll(STORES.PHOTO_UPLOADS);
  return {
    pending: all.filter(p => p.status === 'pending').length,
    failed: all.filter(p => p.status === 'failed').length,
  };
}

export async function discardPhotoUpload(photoId) {
  await remove(STORES.PHOTO_UPLOADS, photoId);
  logSync('info', 'photo.discarded', { photo_id: photoId });
}

export async function retryPhotoUpload(photoId) {
  const all = await getAll(STORES.PHOTO_UPLOADS);
  const photo = all.find(p => p.id === photoId);
  if (!photo) return false;
  photo.status = 'pending';
  photo.retries = 0;
  photo.last_error = null;
  photo.last_error_class = null;
  photo.next_attempt_at = new Date().toISOString();
  await put(STORES.PHOTO_UPLOADS, photo);
  return true;
}

function classifyError(err) {
  const status = err?.response?.status;
  if (status === 401 || status === 403) return 'auth';
  if (status >= 400 && status < 500) return 'validation';
  if (status >= 500 && status < 600) return 'server';
  if (err?.code === 'ECONNABORTED' || err?.message?.includes('timed out')) return 'network';
  if (!err?.response) return 'network';
  return 'unknown';
}

function nextBackoffMs(retries) {
  return BACKOFF_LADDER_MS[Math.min(retries, BACKOFF_LADDER_MS.length - 1)];
}

function dataUrlToBlob(dataUrl) {
  const [header, b64] = dataUrl.split(',');
  const mime = (header.match(/:(.*?);/) || [, 'image/jpeg'])[1];
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

async function withTimeout(promise, ms, label) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)),
  ]);
}

/** Upload a single photo. Returns { ok, server_url, error_class }. */
async function uploadOne(photo) {
  try {
    const blob = dataUrlToBlob(photo.data_url);
    const formData = new FormData();
    formData.append('file', blob, photo.name);
    formData.append('name', photo.name);
    formData.append('tag', photo.tag || '');
    const res = await withTimeout(
      api.post(
        `/organizations/${photo.org_id}/site-visits/${photo.site_visit_id}/upload-field-photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: PHOTO_UPLOAD_TIMEOUT_MS }
      ),
      PHOTO_UPLOAD_TIMEOUT_MS,
      'uploadFieldPhoto'
    );
    return { ok: true, server_url: res.data?.url, server_id: res.data?.id };
  } catch (err) {
    const cls = classifyError(err);
    const message = (err.response?.data?.detail || err.message || 'Unknown error').toString().slice(0, 500);
    return { ok: false, error_class: cls, error: message };
  }
}

/**
 * Background photo processor. Walks the queue once, yielding to the main thread
 * between items so the UI stays responsive on iPad Safari.
 *
 * `force=true` skips the per-item backoff check — used when the USER manually
 * taps "Sync now" / "Retry". Without this, photos pushed out to a 15-min
 * backoff window stay invisible to the user forever.
 *
 * Returns { uploaded: [{photoId, serverUrl, siteVisitId}], failed, deferred, total }
 */
export async function processPhotoUploads({ onProgress, force = false } = {}) {
  const now = Date.now();
  const all = await getAll(STORES.PHOTO_UPLOADS);

  // On user-forced retry, ALSO re-arm any photos that previously gave up
  // (status='failed'). These came from validation/auth errors but a user
  // tapping "Retry" wants us to try again regardless. Without this, a
  // transient 4xx (server hiccup, expired token earlier) leaves the photo
  // permanently stuck even after the underlying issue clears.
  if (force) {
    const nowIso = new Date().toISOString();
    let armed = 0;
    for (const p of all) {
      const isPending = p.status === 'pending';
      const isFailed = p.status === 'failed';
      const backedOff = isPending && p.next_attempt_at && new Date(p.next_attempt_at).getTime() > now;
      if (isFailed || backedOff) {
        p.status = 'pending';
        p.next_attempt_at = nowIso;
        p.last_error = null;
        p.last_error_class = null;
        // Keep retries history — diagnostics value.
        await put(STORES.PHOTO_UPLOADS, p);
        armed++;
      }
    }
    if (armed) logSync('info', 'photo.force_retry_armed', { armed });
  }

  // Re-read after the force-retry rearm so we pick up reset rows.
  const pending = (force ? await getAll(STORES.PHOTO_UPLOADS) : all).filter(p => p.status === 'pending');

  let uploaded = [];
  let failed = 0;
  let deferred = 0;

  for (let i = 0; i < pending.length; i++) {
    const photo = pending[i];

    // Respect backoff UNLESS user-forced retry
    if (!force && photo.next_attempt_at && new Date(photo.next_attempt_at).getTime() > now) {
      deferred++;
      continue;
    }

    // Yield to the main thread so React can paint, touches stay responsive
    // (this is the SolidHome "soft-lock during sync" fix)
    await new Promise(r => setTimeout(r, 0));

    const result = await uploadOne(photo);
    if (result.ok) {
      uploaded.push({
        photoId: photo.id,
        serverUrl: result.server_url,
        serverId: result.server_id,
        siteVisitId: photo.site_visit_id,
      });
      await remove(STORES.PHOTO_UPLOADS, photo.id);
      logSync('info', 'photo.uploaded', { photo_id: photo.id, site_visit_id: photo.site_visit_id });
    } else {
      photo.retries = (photo.retries || 0) + 1;
      photo.last_error = result.error;
      photo.last_error_class = result.error_class;
      if (result.error_class === 'auth' || result.error_class === 'validation') {
        photo.status = 'failed';
        failed++;
        logSync('error', 'photo.failed', { photo_id: photo.id, error_class: result.error_class, error: result.error });
      } else {
        const wait = nextBackoffMs(photo.retries - 1);
        photo.next_attempt_at = new Date(now + wait).toISOString();
        deferred++;
        logSync('warn', 'photo.deferred', { photo_id: photo.id, retry: photo.retries, wait_ms: wait, error_class: result.error_class });
      }
      await put(STORES.PHOTO_UPLOADS, photo);
    }
    onProgress?.({ current: i + 1, total: pending.length });
  }

  return { uploaded, failed, deferred, total: pending.length };
}
